import { useEffect } from 'react'
import { useThemeStore } from '@/stores/themeStore'
import { ThemeProviderContext } from '@/contexts/theme-context'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string // Kept for backwards compatibility but not used
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  ...props
}: ThemeProviderProps) {
  // Get theme state from Zustand store
  const { theme, setTheme } = useThemeStore()

  // Apply theme to document root
  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light'

      root.classList.add(systemTheme)

      // Listen for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => {
        root.classList.remove('light', 'dark')
        root.classList.add(mediaQuery.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    root.classList.add(theme)
  }, [theme])

  // Initialize theme on first mount if not set
  useEffect(() => {
    const storedTheme = useThemeStore.getState().theme
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!storedTheme) {
      setTheme(defaultTheme)
    }
  }, [defaultTheme, setTheme])

  const value = {
    theme,
    setTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}
